import { format } from 'date-fns';
import { useEffect, useState } from 'react';

import { Report } from '@legacy-types';

import { ajaxErrorHandler } from '@legacy-libs/utils';

import { generateTimeTrackingGroupReport } from '@shared/time-tracking-entries/endpoints/exports/generateTimeTrackingGroupReport';
import { getTimeTrackingGroupReport } from '@shared/time-tracking-entries/endpoints/exports/getTimeTrackingGroupReport';
import { Button } from '@shared/ui/components/Button';
import { Icon } from '@shared/ui/components/Icon';
import { Portlet } from '@shared/ui/components/Portlet';
import { cn } from '@shared/utils/helpers';

type Props = {
  groupId: number;
  className?: string;
};

const ExportTimeTrackingEntriesButton = ({ groupId, className }: Props) => {
  const [report, setReport] = useState<null | Report>(null);
  const [inProgress, setInProgress] = useState(false);

  useEffect(() => {
    const fetchReport = async () => {
      const report: any = await getTimeTrackingGroupReport({ id: groupId });
      setReport(report);
      setInProgress(report.in_progress);
    };
    fetchReport();
  }, [groupId]);

  useEffect(() => {
    if (inProgress) {
      const interval = setInterval(async () => {
        const report: any = await getTimeTrackingGroupReport({ id: groupId });
        setReport(report);

        if (!report.in_progress) {
          clearInterval(interval);
          setInProgress(false);
          // Automatically download when generation is complete
          if (report.download_url) {
            downloadReport(report);
          }
        }
      }, 5000); // Poll every 5 seconds

      return () => clearInterval(interval);
    }
  }, [inProgress]);

  function downloadReport(report: Report) {
    const link = document.createElement('a');
    link.href = report.download_url;
    link.download = report.name + format(new Date(report.last_generated_at!), 'dd-MM-yyyy-HH-mm-ss') + '.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  async function handleExport() {
    setInProgress(true);
    generateTimeTrackingGroupReport(groupId);
  }

  if (!report) {
    return null;
  }

  return (
    <Button type="button" disabled={inProgress} onClick={handleExport}>
      {!inProgress && <Icon icon="download" />}
      {inProgress && <Icon icon="spinner" className="animate-spin" />}
      {inProgress ? 'Generating export...' : 'Download time tracking entries'}
    </Button>
  );
};

export default ExportTimeTrackingEntriesButton;
